(function() {
  'use strict';

  const whenVisible = window.theme?.whenVisible || ((element, callback) => callback(element));

  function parseDecimal(value, fallback) {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  }

  function parseIntValue(value, fallback) {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function parseDelayMs(value, fallbackSeconds) {
    const parsed = parseFloat(value);

    if (!Number.isFinite(parsed) || parsed < 0) {
      return fallbackSeconds * 1000;
    }

    if (parsed > 10) {
      return parsed;
    }

    return parsed * 1000;
  }

  if (customElements.get('key-ingredients-v2')) return;

  class KeyIngredientsV2 extends HTMLElement {
    constructor() {
      super();
      this._swiper = null;
      this._swiperPromise = null;
      this._visibilityObserver = null;
    }

    connectedCallback() {
      this._visibilityObserver = whenVisible(this, () => {
        if (this.dataset.layout === 'slider') {
          this._initSlider();
        }
      });
    }

    disconnectedCallback() {
      this._visibilityObserver?.disconnect?.();
      this._destroySlider();
    }

    _initSlider() {
      if (this.dataset.layout !== 'slider') return;
      if (this._swiper || this._swiperPromise) return;

      const swiperElement = this.querySelector('.key-ingredients-v2__swiper');
      if (!swiperElement || typeof window.loadThemeSwiper !== 'function') return;

      const slideCount = swiperElement.querySelectorAll('.swiper-slide').length;
      if (!slideCount) return;

      this._swiperPromise = window.loadThemeSwiper()
        .then(() => {
          if (!this.isConnected || this._swiper) return;

          const slidesDesktop = parseDecimal(this.dataset.slidesDesktop, 4);
          const slidesTablet = parseDecimal(this.dataset.slidesTablet, 3);
          const slidesMobile = parseDecimal(this.dataset.slidesMobile, 1.2);
          const spaceBetweenDesktop = parseIntValue(this.dataset.spaceBetweenDesktop, 30);
          const spaceBetweenMobile = parseIntValue(this.dataset.spaceBetweenMobile, 16);
          const speed = parseIntValue(this.dataset.speed, 6000);
          const showNavigation = this.dataset.showNavigation === 'true';
          const showPagination = this.dataset.showPagination === 'true';
          const navPrev = this.querySelector('.key-ingredients-v2__nav--prev');
          const navNext = this.querySelector('.key-ingredients-v2__nav--next');
          const paginationEl = this.querySelector('.key-ingredients-v2__pagination');
          const loopEnabled = this.dataset.loop !== 'false';
          const maxVisibleSlides = Math.ceil(Math.max(slidesDesktop, slidesTablet, slidesMobile));
          const loop = loopEnabled && slideCount > maxVisibleSlides;
          const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          const autoplayEnabled = this.dataset.autoplay === 'true' && !reducedMotion;

          this.classList.toggle('key-ingredients-v2--marquee', autoplayEnabled && loop && parseDelayMs(this.dataset.autoplayDelay, 0) === 0);

          this._swiper = new Swiper(swiperElement, {
            slidesPerView: slidesDesktop,
            spaceBetween: spaceBetweenDesktop,
            loop: loop,
            speed: speed,
            watchOverflow: true,
            allowTouchMove: true,
            grabCursor: true,
            navigation: showNavigation && navPrev && navNext
              ? {
                  prevEl: navPrev,
                  nextEl: navNext
                }
              : false,
            pagination: showPagination && paginationEl
              ? {
                  el: paginationEl,
                  clickable: true
                }
              : false,
            autoplay: autoplayEnabled
              ? {
                  delay: parseDelayMs(this.dataset.autoplayDelay, 0),
                  disableOnInteraction: false,
                  pauseOnMouseEnter: this.dataset.autoplayPauseOnHover !== 'false'
                }
              : false,
            breakpoints: {
              0: {
                slidesPerView: slidesMobile,
                spaceBetween: spaceBetweenMobile
              },
              750: {
                slidesPerView: slidesTablet,
                spaceBetween: spaceBetweenDesktop
              },
              990: {
                slidesPerView: slidesDesktop,
                spaceBetween: spaceBetweenDesktop
              }
            }
          });
        })
        .catch((error) => {
          console.warn('Swiper failed to load for KeyIngredientsV2:', error);
        })
        .finally(() => {
          this._swiperPromise = null;
        });
    }

    _destroySlider() {
      this.classList.remove('key-ingredients-v2--marquee');

      if (this._swiper) {
        this._swiper.destroy(true, true);
        this._swiper = null;
      }
    }
  }

  customElements.define('key-ingredients-v2', KeyIngredientsV2);

  document.addEventListener('shopify:section:load', function(event) {
    const section = event.target.querySelector('key-ingredients-v2');
    if (section?.dataset.layout === 'slider') {
      section._initSlider?.();
    }
  });
})();
